import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import SectionHeader from "../components/ui/SectionHeader";
import OrderFormPanel from "../components/orders/OrderFormPanel";
import {
  useCreateOrderMutation,
  useCreatePaymentMutation,
  useGetCustomersQuery,
  useGetEmployeesQuery,
} from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";

const initialForm = {
  customer_id: "",
  title: "",
  product_type: "",
  description: "",
  quantity: "",
  unit_price: "",
  priority: "medium",
  deadline: "",
  assigned_employee_id: "",
  order_status: "pending",
  // payment fields
  payment_mode: "none",   // none | half | full | custom
  paid_amount: 0,
  payment_method: "cash",
};

const CreateOrderPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [submitError, setSubmitError] = useState("");
  const { data: customers } = useGetCustomersQuery();
  const { data: employees } = useGetEmployeesQuery();
  const [createOrder] = useCreateOrderMutation();
  const [createPayment] = useCreatePaymentMutation();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    try {
      // 1. Create the order (paid_amount starts at 0 — payment recorded separately)
      const orderPayload = {
        customer_id:          form.customer_id,
        title:                form.title,
        product_type:         form.product_type,
        description:          form.description,
        quantity:             form.quantity,
        unit_price:           form.unit_price,
        priority:             form.priority || "medium",
        deadline:             form.deadline || null,
        assigned_employee_id: form.assigned_employee_id || null,
        order_status:         "pending",
        paid_amount:          0,
      };

      const orderResult = await createOrder(orderPayload).unwrap();
      const orderId = orderResult?.data?.id ?? orderResult?.id;

      // 2. If a payment was made at creation, record it
      const paidAmount = Number(form.paid_amount || 0);
      if (form.payment_mode !== "none" && paidAmount > 0 && orderId) {
        await createPayment({
          order_id:         orderId,
          amount:           paidAmount,
          payment_method:   form.payment_method || "cash",
          reference_number: "",
          notes:            `Initial payment — ${form.payment_mode} payment at order creation`,
        }).unwrap();
      }

      navigate(`/orders/${orderId}`);
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to create order."));
    }
  };

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Orders"
        title="Create a new order"
        description="Fill in the details below. You can record a full or partial payment right now."
      />
      <OrderFormPanel
        form={form}
        customers={customers}
        employees={employees}
        submitError={submitError}
        onSubmit={handleSubmit}
        onChange={setForm}
        onCancel={() => navigate("/orders")}
      />
    </div>
  );
};

export default CreateOrderPage;
